import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, BarChart3, Settings, Link2, Menu, X, CreditCard, MessageSquare, Mail, Phone, Coins, Receipt, Sparkles, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { canAccess } from '../utils/permissions';

// Build navigation based on permissions
const getNavigation = (user) => {
  const nav = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, show: true },
  ];
  
  // Cointracker - admin only
  if (user?.role === 'admin') {
    nav.push({ name: 'Cointracker', href: '/cointracker', icon: Coins, show: true });
  }
  
  // Email tab - for managers with email permission
  if (user?.role === 'admin' || canAccess(user, 'emails')) {
    nav.push({ name: 'Email', href: '/email', icon: Mail, show: true });
  }
  
  // Numbers tab - for admin, head manager, team leader, account creation VA
  if (user?.role === 'admin' || canAccess(user, 'numbers')) {
    nav.push({ name: 'Numbers', href: '/numbers', icon: Phone, show: true });
  }
  
  // VAs tab - for admin or VAs with management permissions
  if (user?.role === 'admin' || canAccess(user, 'vas')) {
    nav.push({ name: 'VAs', href: '/vas', icon: Users, show: true });
  }
  
  // Links tab - for admin or VAs with links permission
  if (user?.role === 'admin' || canAccess(user, 'links')) {
    nav.push({ name: 'Links', href: '/links', icon: Link2, show: true });
  }
  
  // Invoices - everyone
  nav.push({ name: 'Invoices', href: '/invoices', icon: FileText, show: true });
  
  // Bills - admin only
  if (user?.role === 'admin') {
    nav.push({ name: 'Bills', href: '/bills', icon: Receipt, show: true });
  }
  
  // Payments - admin only
  if (user?.role === 'admin' || canAccess(user, 'payments')) {
    nav.push({ name: 'Payments', href: '/payments', icon: CreditCard, show: true });
  }
  
  // Analytics - admin or VAs with analytics permission
  if (user?.role === 'admin' || canAccess(user, 'analytics')) {
    nav.push({ name: 'Analytics', href: '/analytics', icon: BarChart3, show: true });
  }
  
  // CupidBot - admin or Head Manager VA only (same as analytics permission)
  if (user?.role === 'admin' || canAccess(user, 'analytics')) {
    nav.push({ name: 'CupidBot', href: '/cupidbot', icon: MessageSquare, show: true });
  }
  
  // Generation - admin, IG VA, FB VA
  if (user?.role === 'admin' || canAccess(user, 'generation')) {
    nav.push({ name: 'Generation', href: '/generation', icon: Sparkles, show: true });
  }

  // Account Creation - admin + Account Creation VA
  if (user?.role === 'admin' || user?.va_role === 'Account Creation VA') {
    nav.push({ name: 'Account Creation', href: '/account-creation', icon: UserPlus, show: true });
  }
  
  if (user?.role === 'admin') {
    nav.push({ name: 'Chatting', href: '/chatting', icon: MessageSquare, show: true });
  }

  // Settings - admin has Settings, VAs have VA Settings
  if (user?.role === 'admin') {
    nav.push({ name: 'Settings', href: '/settings', icon: Settings, show: true });
  } else {
    nav.push({ name: 'Settings', href: '/va-settings', icon: Settings, show: true });
  }
  
  return nav;
};

export default function Sidebar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const filteredNav = getNavigation(user);

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-white/5">
        <h1 className="text-2xl font-bold font-heading text-white tracking-tight">VA CENTER</h1>
        <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">Command Center</p>
      </div>
      
      <nav className="flex-1 p-4 space-y-2">
        {filteredNav.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/'}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200 ${
                isActive
                  ? 'bg-white text-black font-semibold'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`
            }
            data-testid={`nav-${item.name.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );

  return (
    <>
      <aside className="hidden md:flex md:flex-col md:w-64 bg-black/90 backdrop-blur-md border-r border-white/5">
        <NavContent />
      </aside>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild className="md:hidden fixed top-4 left-4 z-50">
          <Button variant="ghost" size="icon" className="text-white">
            <Menu className="w-6 h-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0 bg-black/90 backdrop-blur-md border-white/5">
          <NavContent />
        </SheetContent>
      </Sheet>
    </>
  );
}